import { ArrowLeftIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useGetLease } from '@/api/units/get-lease'
import { useGetUnitLeases } from '@/api/units/get-unit-leases'
import { FieldList, FieldRow } from '@/components/layout/field-list'
import { Badge } from '@/components/ui/badge'
import { ErrorMessage } from '@/components/ui/error-message'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { Table } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { formatPrice } from '@/utils/currency'
import { formatDateLong, formatDayOfMonthOrdinal } from '@/utils/date'
import { getLeaseHolder, sortLeaseResidents } from '@/utils/units'

import { getLeaseStatusColor, getLeaseStatusLabel } from '../../units.page.utils'
import { getLeaseResidentsColumns } from './lease-details.page.constants'
import * as S from './lease-details.page.styled'
import { getLeaseDaysLeft, getLeaseTermMonths } from './lease-details.page.utils'

interface FieldConfig {
  key: string
  label: string
  value: string
}

export const LeaseDetailsPage = () => {
  const { t } = useTranslation(['units', 'common'])
  const { unitId, leaseId } = useParams<{ unitId: string; leaseId: string }>()

  const { data: leases, isLoading: isLeaseLoading, error } = useGetUnitLeases({ unitId: unitId! })
  const lease = leases?.find(({ id }) => String(id) === leaseId)
  const { data: details, isError: isDetailsError } = useGetLease({ leaseId: leaseId! })

  const pmsTooltip = t('units:detail.general.pmsTooltip')
  const leaseHolder = lease ? getLeaseHolder(lease.residents) : null
  const termMonths = getLeaseTermMonths(lease)
  const daysLeft = getLeaseDaysLeft(lease)
  const financial = details?.financial

  const termLabel = (months: number | null) =>
    months === null ? EMPTY_VALUE : t('units:lease.term.months', { count: months })

  const termFields: FieldConfig[] = [
    {
      key: 'start',
      label: t('units:lease.term.fields.start'),
      value: formatDateLong(lease?.leaseStart),
    },
    {
      key: 'end',
      label: t('units:lease.term.fields.end'),
      value: formatDateLong(lease?.leaseEnd),
    },
    {
      key: 'term',
      label: t('units:lease.term.fields.term'),
      value:
        termMonths !== null && daysLeft !== null
          ? t('units:lease.term.monthsWithDaysLeft', {
              count: daysLeft,
              months: termLabel(termMonths),
            })
          : termLabel(termMonths),
    },
    {
      key: 'signedDate',
      label: t('units:lease.term.fields.signedDate'),
      value: formatDateLong(details?.signedDate),
    },
    {
      key: 'leaseType',
      label: t('units:lease.term.fields.leaseType'),
      value: details?.leaseType || EMPTY_VALUE,
    },
  ]

  const financialFields: FieldConfig[] = [
    {
      key: 'term',
      label: t('units:lease.financial.fields.term'),
      value: termLabel(financial?.termMonths ?? termMonths),
    },
    {
      key: 'monthlyRent',
      label: t('units:lease.financial.fields.monthlyRent'),
      value: formatPrice(financial?.monthlyRentCents),
    },
    {
      key: 'rentDue',
      label: t('units:lease.financial.fields.rentDue'),
      value:
        financial?.rentDueDay && financial.rentFrequency
          ? t('units:lease.financial.rentDueValue', {
              day: formatDayOfMonthOrdinal(financial.rentDueDay),
              frequency: financial.rentFrequency,
            })
          : EMPTY_VALUE,
    },
    {
      key: 'arBalance',
      label: t('units:lease.financial.fields.arBalance'),
      value: formatPrice(financial?.arBalanceCents),
    },
    {
      key: 'securityDeposit',
      label: t('units:lease.financial.fields.securityDeposit'),
      value: formatPrice(financial?.securityDepositCents),
    },
  ]

  const renderFields = (fields: FieldConfig[]) => (
    <FieldList>
      {fields.map(({ key, label, value }) => (
        <FieldRow key={key} label={label} pmsTooltip={pmsTooltip} value={value} />
      ))}
    </FieldList>
  )

  return (
    <S.Card loading={isLeaseLoading}>
      <S.Header>
        <Link
          size="small"
          use="secondary"
          to={{ params: { unitId: unitId! }, path: '/dashboard/units/:unitId/leases' }}
        >
          <Icon icon={ArrowLeftIcon} size="1.4rem" />
          {t('common:actions.back')}
        </Link>
        <Typography as="h2" color="black800" size="heading-small" weight="semibold">
          {t('units:lease.header.leaseNumber', { leaseId: lease?.pmsLeaseId ?? '' })}
        </Typography>
        {lease?.leaseStatus && (
          <Badge color={getLeaseStatusColor(lease.leaseStatus)} size="small" variant="light">
            {getLeaseStatusLabel(lease.leaseStatus, t)}
          </Badge>
        )}
      </S.Header>

      <S.Section>
        <Typography as="h3" color="black800" size="heading-small" weight="semibold">
          {t('units:lease.residents.title')}
        </Typography>
        <Table
          columns={getLeaseResidentsColumns({ leaseHolder, t })}
          data={sortLeaseResidents(lease?.residents ?? [])}
          emptyDescription={t('units:lease.residents.empty.description')}
          emptyText={t('units:lease.residents.empty.title')}
          error={error}
          getRowKey={(resident) =>
            `${resident.clientId ?? ''}-${resident.email ?? ''}-${resident.moveInDate ?? ''}`
          }
        />
      </S.Section>

      {isDetailsError || (!isLeaseLoading && !lease) ? (
        <ErrorMessage>{t('units:lease.error')}</ErrorMessage>
      ) : (
        <>
          <S.Section>
            <Typography as="h3" color="black800" size="heading-small" weight="semibold">
              {t('units:lease.term.title')}
            </Typography>
            {renderFields(termFields)}
          </S.Section>

          <S.Section>
            <Typography as="h3" color="black800" size="heading-small" weight="semibold">
              {t('units:lease.financial.title')}
            </Typography>
            {renderFields(financialFields)}
          </S.Section>
        </>
      )}
    </S.Card>
  )
}
